import React from 'react';
import type { PropsWithChildren } from 'react';

import { GameDocumentContext } from './game-document';
import { TimerEntity } from '../types/game-document/entities';
import { ScoringReductionEntity } from '../types/entities/task';
import { TaskContentForm } from '../types/game-document/entities/task-content';

export type Mode = 'down' | 'up';
export type Status = 'idle' | 'running' | 'paused' | 'finished';

export interface Timer {
  id: string;
  mode: Mode;
  durationMs: number;
  startAt: number | null;
  elapsedMs: number;
  status: Status;
  isFormTimer: boolean;
}

export interface TimerHandlers {
  /** timer started, resumed, or restarted. */
  onStart?: (timer: Timer) => void;
  /** a running timer was was paused or stopped before reaching its limit. */
  onStop?: (timer: Timer) => void;
  /** the timer ran out (reached its limit). */
  onElapsed?: (timer: Timer) => void;
}

type EventName = keyof TimerHandlers;

const elapsedOf = (t: Timer, at: number) =>
  t.elapsedMs +
  (t.status === 'running' && t.startAt !== null
    ? Math.max(0, at - t.startAt)
    : 0);

const isDone = (t: Timer, at: number) =>
  t.durationMs > 0 && elapsedOf(t, at) >= t.durationMs;

/** Whole seconds shown to the user. */
const displaySeconds = (t: Timer, at: number) => {
  const elapsed = elapsedOf(t, at);
  if (t.mode === 'down') {
    return Math.ceil(Math.max(0, t.durationMs - elapsed) / 1000);
  }
  return Math.floor(
    (t.durationMs > 0 ? Math.min(elapsed, t.durationMs) : elapsed) / 1000
  );
};

export const formatSeconds = (total: number) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const h = Math.floor(total / 3600);
  return `${h ? h + ':' : ''}${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
};

/**
 * Marks due timers as finished. Returns the same array if nothing changed.
 * Newly finished timers are pushed to `finished` (when provided).
 */
const finishDue = (list: Timer[], at: number, finished?: Timer[]): Timer[] => {
  let changed = false;
  const next = list.map((t) => {
    if (t.status === 'running' && isDone(t, at)) {
      changed = true;
      const done: Timer = {
        ...t,
        status: 'finished',
        startAt: null,
        elapsedMs: t.durationMs
      };
      finished?.push(done);
      return done;
    }
    return t;
  });
  return changed ? next : list;
};

const TICK_MS = 250;
const isBrowser = typeof window !== 'undefined';
type DocTimer = TimerEntity & { isFormTimer: boolean };
const TIMERS_KEY_PREFIX = 'timers-';

/** Removes saved timers of every other project, keeping only `currentKey`. */
const clearOtherTimerKeys = (currentKey: string) => {
  if (!isBrowser) return;
  try {
    // Collect first: removing while iterating shifts the indexes.
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(TIMERS_KEY_PREFIX) && k !== currentKey)
        stale.push(k);
    }
    stale.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage blocked: nothing to clean */
  }
};

function createTimerStore(
  key: string,
  timersFromDocs: DocTimer[],
  getGlobalHandlers: () => TimerHandlers,
  enabled = true
) {
  // cleaning all "timers-" in local storage first before create a new one for opened game
  if (enabled) clearOtherTimerKeys(key);

  const fromDocs = (doc: DocTimer): Timer => ({
    id: doc.id,
    durationMs: (doc.interval ?? 0) * 1000,
    mode: doc.direction,
    elapsedMs: 0,
    startAt: null,
    status: 'idle',
    isFormTimer: doc.isFormTimer
  });

  const load = (): Timer[] => {
    if (!enabled) return timersFromDocs.map(fromDocs);

    let stored: Timer[] = [];

    if (isBrowser) {
      try {
        const raw = localStorage.getItem(key);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        if (Array.isArray(parsed)) stored = parsed as Timer[];
      } catch {
        console.error('Corrupted timer data');
      }
    }

    const savedById = new Map(stored.map((t) => [t.id, t]));

    const merged = timersFromDocs.map((doc): Timer => {
      const fresh = fromDocs(doc);
      const saved = savedById.get(doc.id);

      // Keep the saved progress only if the docs' config hasn't changed.
      if (
        saved &&
        saved.mode === fresh.mode &&
        saved.durationMs === fresh.durationMs
      ) {
        return { ...saved, isFormTimer: doc.isFormTimer };
      }
      return fresh;
    });

    // Timers that ran out while the page was closed or away become finished.
    return finishDue(merged, Date.now());
  };

  let timers: Timer[] = load();
  let now = Date.now();
  let interval: ReturnType<typeof setInterval> | null = null;
  const listeners = new Set<() => void>();
  const timerHandlers = new Map<string, Set<() => TimerHandlers>>();

  const persist = () => {
    if (!enabled) return;
    try {
      localStorage.setItem(key, JSON.stringify(timers));
    } catch {
      /* local storage full or blocked: keep working in memory */
    }
  };

  persist(); // initial save (no-op when disabled)

  // ONE interval for every timer in this store. It only exists while
  // something is subscribed AND something is running.
  const syncInterval = () => {
    if (!enabled) return;
    const needed =
      listeners.size > 0 && timers.some((t) => t.status === 'running');
    if (needed && !interval) interval = setInterval(tick, TICK_MS);
    else if (!needed && interval) {
      clearInterval(interval);
      interval = null;
    }
  };

  const emit = () => listeners.forEach((l) => l());

  const commit = (next: Timer[], at = Date.now()) => {
    timers = next;
    now = at;
    persist();
    syncInterval();
    emit();
  };

  /** Calls the provider-level handler first, then this timer's own handlers. */
  const fire = (event: EventName, timer: Timer) => {
    getGlobalHandlers()[event]?.(timer);
    timerHandlers.get(timer.id)?.forEach((get) => get()[event]?.(timer));
  };

  function tick() {
    const at = Date.now();
    const finished: Timer[] = [];
    const next = finishDue(timers, at, finished);
    if (next === timers) {
      now = at;
      emit(); // subscribers only re-render if their own snapshot changed
      return;
    }
    commit(next, at);
    finished.forEach((t) => fire('onElapsed', t));
  }

  const registerHandlers = (id: string, get: () => TimerHandlers) => {
    let set = timerHandlers.get(id);
    if (!set) timerHandlers.set(id, (set = new Set()));
    set.add(get);
    return () => {
      set.delete(get);
      if (set.size === 0) timerHandlers.delete(id);
    };
  };

  /**
   * Applies a change to one timer. `event` decides, from the timer's previous
   * state, which event (if any) to fire after the change is committed.
   */
  const patch = (
    id: string,
    event: (prev: Timer) => EventName | null,
    fn: (t: Timer, at: number) => Timer
  ) => {
    if (!enabled) return;

    const at = Date.now();
    const idx = timers.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const prev = timers[idx];
    const next = fn(prev, at);
    if (next === prev) return; // nothing changed: no commit, no events

    commit(
      timers.map((t, i) => (i === idx ? next : t)),
      at
    );

    const name = event(prev);
    if (name) fire(name, next);
  };

  /** Applies a change to every timer in one commit, then fires `event` for each timer that changed. */
  const patchAll = (event: EventName, fn: (t: Timer, at: number) => Timer) => {
    if (!enabled) return;

    const at = Date.now();
    const changed: Timer[] = [];

    const next = timers.map((t) => {
      const updated = fn(t, at);
      if (updated !== t) changed.push(updated);
      return updated;
    });

    if (changed.length === 0) return; // nothing to change: no commit, no events

    commit(next, at);
    changed.forEach((t) => fire(event, t));
  };

  const toPaused = (t: Timer, at: number): Timer =>
    t.status !== 'running' || t.startAt === null
      ? t
      : {
          ...t,
          status: 'paused',
          startAt: null,
          elapsedMs: Math.min(elapsedOf(t, at), t.durationMs || Infinity)
        };

  const toIdle = (t: Timer): Timer =>
    t.status === 'idle' && t.elapsedMs === 0
      ? t
      : { ...t, status: 'idle', startAt: null, elapsedMs: 0 };

  const toRunning = (t: Timer, at: number): Timer =>
    t.status !== 'paused' ? t : { ...t, status: 'running', startAt: at };

  const actions = {
    start: (id: string) =>
      patch(
        id,
        () => 'onStart',
        (t, at) =>
          t.status === 'running'
            ? t
            : {
                ...t,
                status: 'running',
                startAt: at,
                elapsedMs: t.status === 'finished' ? 0 : t.elapsedMs
              }
      ),

    // Pause halts a running timer before its limit, so it fires onStop.
    pause: (id: string) => patch(id, () => 'onStop', toPaused),

    // Stop resets the timer. It fires onStop only if the timer was running;
    // a paused timer already reported onStop when it was paused.
    stop: (id: string) =>
      patch(
        id,
        (prev) => (prev.status === 'running' ? 'onStop' : null),
        (t) => toIdle(t)
      ),

    remove: (id: string) => {
      if (enabled) commit(timers.filter((t) => t.id !== id));
    },

    pauseAll: () => patchAll('onStop', toPaused),
    stopAll: () =>
      patchAll('onStop', (t) =>
        t.status === 'running' || t.status === 'paused' ? toIdle(t) : t
      ),
    resumeAll: () => patchAll('onStart', toRunning)
  };

  return {
    enabled,
    actions,
    registerHandlers,
    getTimers: () => timers,
    getTimer: (id: string) => timers.find((t) => t.id === id),
    getSeconds: (id: string) => {
      const t = timers.find((x) => x.id === id);
      return t ? displaySeconds(t, now) : 0;
    },
    subscribe(listener: () => void) {
      if (!enabled) return () => {};
      listeners.add(listener);
      syncInterval();
      return () => {
        listeners.delete(listener);
        syncInterval();
      };
    }
  };
}

type TimerStore = ReturnType<typeof createTimerStore>;

const TimersContext = React.createContext<TimerStore | null>(null);

interface TimersProviderProps extends TimerHandlers {
  gameCode: string;
  isFromFacilitator?: boolean;
}

export const TimersProvider = ({
  gameCode,
  children,
  isFromFacilitator,
  onStop,
  onStart,
  onElapsed
}: TimersProviderProps & PropsWithChildren) => {
  const [state] = React.useContext(GameDocumentContext);
  const docTimers = state.gameDocument?.assets.timers;
  const taskContents = state.gameDocument?.assets.taskContents;

  const timersFromDocs = React.useMemo<DocTimer[]>(
    () => (docTimers ?? []).map((t) => ({ ...t, isFormTimer: false })),
    [docTimers]
  );

  const timersForm = React.useMemo(
    () =>
      (taskContents ?? []).flatMap((tc) =>
        (tc.forms ?? [])
          .filter(
            (
              f
            ): f is TaskContentForm & {
              scoringReduction: ScoringReductionEntity & {
                maximumTimeLimit: number;
              };
            } =>
              f.isScoringReduction === true &&
              !!f.scoringReduction?.maximumTimeLimit // excludes undefined and 0
          )
          .map((f) => ({
            id: f.id,
            direction: 'down',
            interval: f.scoringReduction.maximumTimeLimit,
            isFormTimer: true
          }))
      ),
    [taskContents]
  ) as DocTimer[];

  const allTimers = React.useMemo(
    () => [...timersFromDocs, ...timersForm],
    [timersFromDocs, timersForm]
  );

  // Rebuild the store only when a timer's id or config really changes,
  // not on every edit to the game document.
  const signature = allTimers
    .map((t) => `${t.id}:${t.direction}:${t.interval ?? 0}`)
    .join('|');

  const handlers = React.useRef<TimerHandlers>({ onStart, onStop, onElapsed });
  React.useEffect(() => {
    handlers.current = { onStart, onStop, onElapsed };
  }, [onStart, onStop, onElapsed]);

  const enabled = !isFromFacilitator;

  const value = React.useMemo(
    () =>
      createTimerStore(
        `timers-${gameCode}`,
        allTimers,
        () => handlers.current,
        enabled
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gameCode, signature, enabled]
  );

  return (
    <TimersContext.Provider value={value}>{children}</TimersContext.Provider>
  );
};

export const useTimerContext = (): TimerStore => {
  const timersContext = React.useContext(TimersContext);
  if (!timersContext)
    throw new Error('Timer hooks must be used inside <TimersProvider>');
  return timersContext;
};
